// R115 guest — inspector sections. Stay: the special-offer flag (B4), the
// recovery state (B2) and the guest conversation (B5). Listing: the Airbnb ★
// (B8), the open claims / resolutions / recovery counts, and the jump links
// (B7). Sections render synchronously from what is already known (base data,
// the inspector's data, the module caches) and lazily fetch the rest in
// bind(), patching ONLY their own wrapper (data-gx-sec) — never the core's
// markup. Every fetch is a read; every write goes through a form sheet.
import type { CalendarCtx, EntityRef, Section, ViewId } from '../registry';
import type { Stay } from '../types';
import { soMark, missingText, COMPONENT_LABEL, canOpenSpecialOffers, isAirbnbChannel, stayActions, scoreMain, scoreSecondary, canOpenOptimizer, safeExternal, HOSTAWAY_HOST_RE, AIRBNB_HOST_RE } from '../../../../../lib/calendarActions.mjs';
import { STATE_LABEL, STATE_PILL, stateLabel } from '../../../../../lib/guestRecovery.mjs';
import { detailFrom, stayFrom, propertyOf, listingCards, recoveryTargets, convRefs, getJson, errText, handoff, fmtWhen, money } from './util';
import type { SoLayer, ListingCard } from './util';
import { openRecoverySheet, openMessageSheet } from './sheets';

// "cleaningFee" → "Cleaning fee"; an unknown Hostaway name is shown as it is
const componentLabel = (name: string) => { const l = (COMPONENT_LABEL as Record<string, string>)[name]; return l ? l.charAt(0).toUpperCase() + l.slice(1) : name; };
const wrap = (key: string, id: string, inner: string) => `<div class="gx-sec" data-gx-sec="${key}" data-gx-id="${id}">${inner}</div>`;
const find = (root: HTMLElement, key: string): HTMLElement | null => (root.matches && root.matches(`[data-gx-sec="${key}"]`) ? root : root.querySelector<HTMLElement>(`[data-gx-sec="${key}"]`));

// ── the stay's special-offer state: the detail (when core passes it) beats the layer ──
export function soFor(s: Stay, data: unknown, ctx: CalendarCtx): { state: string; missing?: string[]; money?: any; resolved?: any; checked_at?: string | null; broken_out?: boolean | null; balanced?: boolean | null } | null {
  const d = detailFrom(data);
  if (d && d.special_offer !== undefined) return d.special_offer || null;
  const l = ctx.layer<SoLayer>('special_offers');
  return (l && l.by_stay && l.by_stay[s.id]) || null;
}

export function stayTarget(ref: EntityRef, data: unknown, ctx: CalendarCtx) {
  const s = stayFrom(ref, data, ctx);
  if (!s) return null;
  const d = detailFrom(data);
  const p = propertyOf(ctx, s.listing_id, d);
  return { s, d, listing_name: p ? p.name : s.listing_id };
}

// ════════════════════════════════════════════════════════════════════════
// stay · special offer (B4)
// ════════════════════════════════════════════════════════════════════════
export const specialOfferSection: Section = {
  inspector: 'stay', key: 'special_offer', title: 'Special offer', order: 12, openIn: ['stays', 'guests'],
  when: (ref, data, ctx) => {
    const s = stayFrom(ref, data, ctx);
    if (!s || s.kind !== 'stay' || !isAirbnbChannel(s.channel)) return false;
    const so = soFor(s, data, ctx);
    // R115 review: a "Special offer" section only for a booking that IS one (flagged / recorded / resolved) — "not
    // checked yet" / "check failed" on most Airbnb stays was noise (the nightly check covers 30 days of check-ins)
    if (!so || !['flagged', 'recorded', 'resolved'].includes(so.state)) return false;
    return !!soMark(so);
  },
  render: (ref, data, ctx) => {
    const s = stayFrom(ref, data, ctx)!;
    const so = soFor(s, data, ctx)!;
    const m = soMark(so)!;
    const esc = ctx.esc;
    const comps = so.money && Array.isArray(so.money.components) ? so.money.components : null;
    const canOpen = canOpenSpecialOffers(ctx.role);
    return wrap('special_offer', s.id, `
      <div class="gx-so ${m.tone === 'warn' ? 'warn' : ''}" title="${esc(m.title)}">
        <div class="gx-row"><span class="pill ${m.pill}">${m.glyph ? `${m.glyph} ` : ''}${esc(m.text)}</span>${so.checked_at ? `<span class="gx-faint">checked ${esc(fmtWhen(so.checked_at))}</span>` : ''}</div>
        <div class="gx-sub">${so.state === 'flagged' ? flaggedCopy(so, esc)
          : so.state === 'recorded' ? 'Accounting recorded the fee split for this booking (P&L uses it); it has not been sent to Hostaway yet.'
          : so.state === 'resolved' ? `Marked fine as it is${so.resolved && so.resolved.note ? ` — “${esc(so.resolved.note)}”` : ''}${so.resolved && so.resolved.by ? ` (${esc(so.resolved.by)})` : ''}.`
          : esc(m.title)}</div>
        ${comps && comps.length ? `<table aria-label="Hostaway fee lines">${comps.map((c: any) => `<tr><td>${esc(componentLabel(c.name))}</td><td>${esc(money(c.amount))}</td></tr>`).join('')}<tr><td><b>Total</b></td><td><b>${esc(money(so.money.total))}</b></td></tr></table>` : ''}
        ${canOpen && so.state !== 'unchecked' ? `<div class="gx-btnrow"><button type="button" class="btn btn-secondary btn-sm" data-gx-so-open>Review in Special Offers</button></div>` : ''}
      </div>`);
  },
  bind: (root, ref, data, ctx) => {
    const el = find(root, 'special_offer');
    const s = stayFrom(ref, data, ctx);
    el?.querySelector('[data-gx-so-open]')?.addEventListener('click', () => { if (s) handoff.specialOffers(ctx, s.id, s.check_in); });
  },
};

// R115 review: say WHY it was flagged — a lump (no fee lines), lines that don't add up, or a component this home's
// other Airbnb bookings carry (the layer has no broken_out / balanced: it then speaks only of what is missing)
function flaggedCopy(so: { missing?: string[]; broken_out?: boolean | null; balanced?: boolean | null }, esc: (s: unknown) => string): string {
  const miss = so.missing && so.missing.length ? String(missingText(so.missing)) : '';
  if (so.broken_out === false) return 'Airbnb sent this booking as one lump — its fees are not broken out, so the owner statement and the fee engine read it all as accommodation.';
  if (so.balanced === false) return "Hostaway's fee lines don't add up to the booking total — accounting rebalances it on the Special Offers page.";
  if (miss) return `${esc(miss.charAt(0).toUpperCase() + miss.slice(1))} ${so.missing!.length === 1 ? 'is' : 'are'} missing compared with this home's other Airbnb bookings.`;
  return "This booking's fee breakdown needs a look on the Special Offers page.";
}

// ════════════════════════════════════════════════════════════════════════
// stay · recovery (B2) — the case / thread state, opens the composer sheet
// ════════════════════════════════════════════════════════════════════════
export const recoverySection: Section = {
  inspector: 'stay', key: 'recovery', title: 'Guest recovery', order: 25, openIn: ['guests'],
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).recovery; },
  render: (ref, data, ctx) => {
    const t = stayTarget(ref, data, ctx)!;
    const esc = ctx.esc;
    const cached = recoveryTargets.peek(t.s.id);
    const flag = (t.d && t.d.flagged) || t.s.flagged;
    const hint = flag && flag.recovery_status ? `<span class="pill ${flag.recovery_status === 'open' ? 'pill-red' : 'pill-gray'}">${esc(stateLabel(flag as any))}</span>` : '';
    return wrap('recovery', t.s.id, cached && cached.data ? recoveryInner(ctx, cached.data.target) : `<div class="gx-row"><span class="gx-ic">❤</span><div><div class="gx-t">Guest recovery ${hint}</div><div class="gx-load">Checking for a recovery case…</div></div></div>`);
  },
  bind: (root, ref, data, ctx) => {
    const el = find(root, 'recovery');
    const t = stayTarget(ref, data, ctx);
    if (!el || !t) return;
    const open = () => openRecoverySheet(ctx, { hid: t.s.id, guest: t.s.guest_name, listing_name: t.listing_name, check_in: t.s.check_in, check_out: t.s.check_out });
    const wire = () => el.querySelectorAll<HTMLElement>('[data-gx-rec-open]').forEach(b => b.addEventListener('click', open));
    wire();
    if (recoveryTargets.peek(t.s.id)) return;
    recoveryTargets.get(t.s.id).then(j => {
      if (!el.isConnected) return;
      el.innerHTML = j ? recoveryInner(ctx, j.target) : `<div class="gx-row"><span class="gx-ic">❤</span><div><div class="gx-t">Guest recovery</div><div class="gx-sub">${ctx.esc(errText(recoveryTargets.lastResult(t.s.id), "Couldn't check for a recovery case"))}</div></div></div><div class="gx-btnrow"><button type="button" class="btn btn-secondary btn-sm" data-gx-rec-open>Open recovery</button></div>`;
      wire();
    });
  },
};
function recoveryInner(ctx: CalendarCtx, tg: any): string {
  const esc = ctx.esc;
  if (!tg) {
    return `<div class="gx-row"><span class="gx-ic">❤</span><div><div class="gx-t">No recovery case</div><div class="gx-sub">Start one when a guest needs smoothing over — log what you did, then close it with an outcome.</div></div></div>
      <div class="gx-btnrow"><button type="button" class="btn btn-secondary btn-sm" data-gx-rec-open>Start a recovery case</button></div>`;
  }
  const state = (tg.recovery_state || 'open') as keyof typeof STATE_LABEL;
  const log = Array.isArray(tg.recovery_log) ? tg.recovery_log.filter((e: any) => e && e.text).slice(-2) : [];
  return `<div class="gx-row"><span class="gx-ic">❤</span><div style="min-width:0;"><div class="gx-t">${tg.kind === 'case' ? 'Recovery case' : 'Guest thread on the recovery list'} <span class="pill ${STATE_PILL[state] || 'pill-red'}">${esc(STATE_LABEL[state] || 'Open')}</span></div>
      ${tg.summary ? `<div class="gx-sub">${esc(tg.summary)}</div>` : ''}</div></div>
    ${log.length ? `<div class="gx-mini">${log.map((e: any) => `<div class="gx-faint">${esc(fmtWhen(e.at))} · ${esc(e.by_name || e.by || 'team')}: ${esc(String(e.text).slice(0, 160))}</div>`).join('')}</div>` : ''}
    <div class="gx-btnrow"><button type="button" class="btn btn-secondary btn-sm" data-gx-rec-open>${state === 'open' ? 'Log / close…' : 'Open case…'}</button></div>`;
}

// ════════════════════════════════════════════════════════════════════════
// stay · message (B5) — a preview of the conversation; the sheet replies
// ════════════════════════════════════════════════════════════════════════
export const messageSection: Section = {
  inspector: 'stay', key: 'message', title: 'Messages', order: 30, openIn: ['guests'],
  when: (ref, data, ctx) => { const s = stayFrom(ref, data, ctx); return !!s && stayActions(s).message; },
  render: (ref, data, ctx) => {
    const t = stayTarget(ref, data, ctx)!;
    const auto = ctx.view === 'guests';
    return wrap('message', t.s.id, `
      <div class="gx-row"><span class="gx-ic">💬</span><div><div class="gx-t">Guest conversation</div><div class="gx-sub">${isAirbnbChannel(t.s.channel) ? 'The Airbnb thread, through Hostaway' : 'The booking thread, through Hostaway'} — and SMS when the guest's number is on a Quo line.</div></div></div>
      <div class="gx-mini" data-gx-preview>${auto ? '<div class="gx-load">Loading the latest messages…</div>' : '<button type="button" class="gx-link" data-gx-preview-load>Show the latest messages</button>'}</div>
      <div class="gx-btnrow"><button type="button" class="btn btn-secondary btn-sm" data-gx-msg-open>Reply…</button></div>`);
  },
  bind: (root, ref, data, ctx) => {
    const el = find(root, 'message');
    const t = stayTarget(ref, data, ctx);
    if (!el || !t) return;
    el.querySelector('[data-gx-msg-open]')?.addEventListener('click', () => openMessageSheet(ctx, { hid: t.s.id, guest: t.s.guest_name, listing_name: t.listing_name, check_in: t.s.check_in, check_out: t.s.check_out, channel: t.s.channel }));
    const pv = el.querySelector<HTMLElement>('[data-gx-preview]');
    const show = (html: string) => { if (pv && pv.isConnected) pv.innerHTML = html; };
    const loadPreview = (force = false) => {
      if (!pv) return;
      const hit = !force && previewFor(t.s.id);
      if (hit) { show(hit); return; }
      show('<div class="gx-load">Loading the latest messages…</div>');
      void fetchPreview(ctx, t.s.id, t.s.guest_name, force).then(show);
    };
    pv?.querySelector('[data-gx-preview-load]')?.addEventListener('click', () => loadPreview());
    pv?.addEventListener('click', (e) => { if ((e.target as HTMLElement).closest('[data-gx-preview-retry]')) loadPreview(true); });
    // R115 review: every panel redraw re-binds — the preview for the open stay is memoised (60 s, in memory only) and
    // one request is shared, so a checklist tick no longer re-reads the Hostaway thread 4–6 times
    if (ctx.view === 'guests' || previewFor(t.s.id)) loadPreview();
  },
};
const PREVIEW_TTL = 60 * 1000;
let preview: { hid: string; at: number; html: string } | null = null;   // ONE open stay's rendered preview, never in storage
let previewP: { hid: string; p: Promise<string> } | null = null;
const previewFor = (hid: string): string | null => (preview && preview.hid === hid && Date.now() - preview.at < PREVIEW_TTL ? preview.html : null);
/** A message was sent from the sheet: the next look reloads the thread. */
export function dropPreview(hid?: string): void { if (!hid || (preview && preview.hid === hid)) preview = null; if (!hid || (previewP && previewP.hid === hid)) previewP = null; }
function fetchPreview(ctx: CalendarCtx, hid: string, guestName: string, force: boolean): Promise<string> {
  if (!force && previewP && previewP.hid === hid) return previewP.p;
  const esc = ctx.esc;
  const retry = '<button type="button" class="gx-link" data-gx-preview-retry>↻ Retry</button>';
  const p = (async () => {
    if (force) convRefs.drop(hid);
    const ref = await convRefs.get(hid);
    if (!ref) return `<div class="gx-faint">${esc(errText(convRefs.lastResult(hid), "Couldn't look up the conversation"))} ${retry}</div>`;
    if (!ref.conversation_id) { convRefs.drop(hid); return `<div class="gx-faint">${ref.configured && !ref.configured.pms ? "Hostaway isn't connected on this server." : 'No conversation on this booking yet.'}</div>`; }   // a thread may start any minute — never cache "none"
    const th = await getJson(`/api/messaging/thread?channel=pms&id=${encodeURIComponent(ref.conversation_id)}&reservation_id=${encodeURIComponent(hid)}`);
    const msgs = th.ok && th.json && Array.isArray(th.json.messages) ? th.json.messages : null;
    if (!msgs) return `<div class="gx-faint">${esc(errText(th, "Couldn't load the messages"))} ${retry}</div>`;
    const last = msgs.slice(-3);
    const html = last.length ? last.map((m: any) => `<div class="gx-bub ${m.direction === 'outgoing' ? 'out' : 'in'}">${esc(String(m.body || '').slice(0, 220))}${String(m.body || '').length > 220 ? '…' : ''}<span class="gx-when">${m.direction === 'outgoing' ? 'Us' : esc(guestName)} · ${esc(fmtWhen(m.at))}</span></div>`).join('') : '<div class="gx-faint">No messages on this thread yet.</div>';
    preview = { hid, at: Date.now(), html };
    return html;
  })();
  previewP = { hid, p };
  void p.finally(() => { if (previewP && previewP.p === p) previewP = null; });
  return p;
}

// ════════════════════════════════════════════════════════════════════════
// listing · score / counts / links (B7, B8) — one lazy GET /api/stays/listing/:id
// ════════════════════════════════════════════════════════════════════════
function cardInner(key: 'score' | 'counts' | 'links', card: ListingCard | null, ctx: CalendarCtx, lid: string, failed = false): string {
  const esc = ctx.esc;
  if (!card) return failed ? `<div class="gx-faint">Couldn't load this home's card — try again in a moment.</div>` : '<div class="gx-load">Loading…</div>';
  if (key === 'score') {
    const main = scoreMain(card.scores);
    const sec = scoreSecondary(card.scores);
    return `<div class="gx-score">
      <div class="gx-score-main">${main ? `<span class="gx-star">★</span> ${esc(main.avg)} <span class="gx-sub">Airbnb · ${main.n} review${main.n === 1 ? '' : 's'}</span>` : `<span class="gx-sub">${card.scores_error ? esc(card.scores_error) : 'No Airbnb reviews yet'}</span>`}</div>
      ${sec ? `<div class="gx-faint">${esc(sec)}</div>` : ''}
      <div class="gx-faint">Airbnb-only average of published guest reviews (Hostaway's all-channel number shown as secondary).</div>
    </div>`;
  }
  if (key === 'counts') {
    const n = (v: number | null) => (v == null ? '—' : String(v));
    return `<div class="gx-counts">
      <button type="button" class="gx-count" data-gx-go="claims" title="Open claims (not denied / recovered)"><b>${n(card.counts.open_claims)}</b><span>Open claims</span></button>
      <button type="button" class="gx-count" data-gx-go="resolutions" title="Open resolutions (refunds / extra charges)"><b>${n(card.counts.open_resolutions)}</b><span>Resolutions</span></button>
      <button type="button" class="gx-count" data-gx-go="recovery" title="Open guest-recovery cases / threads"><b>${n(card.counts.recovery_open)}</b><span>Recovery</span></button>
    </div>`;
  }
  const hx = safeExternal(card.links && card.links.hostaway_listing, HOSTAWAY_HOST_RE);
  const ax = safeExternal(card.links && card.links.airbnb_url, AIRBNB_HOST_RE);
  return `<div class="gx-links">
    <button type="button" class="btn btn-secondary" data-gx-jump="pds">🏠 Property Details</button>
    <button type="button" class="btn btn-secondary" data-gx-jump="rm">📈 AI Revenue Manager</button>
    <button type="button" class="btn btn-secondary" data-gx-jump="reviews">★ Reviews</button>
    <button type="button" class="btn btn-secondary" data-gx-jump="settings">⚙ Property Settings</button>
    ${canOpenOptimizer(ctx.role) ? '<button type="button" class="btn btn-secondary" data-gx-jump="optimizer">✦ AI Optimizer</button>' : ''}
    ${hx ? `<a class="btn btn-secondary gx-ext" href="${esc(hx)}" target="_blank" rel="noopener noreferrer" title="The Hostaway dashboard's listing page">Open in Hostaway</a>` : ''}
    ${ax ? `<a class="btn btn-secondary gx-ext" href="${esc(ax)}" target="_blank" rel="noopener noreferrer">View on Airbnb</a>` : ''}
  </div>`;
}
function listingSection(key: 'score' | 'counts' | 'links', title: string, order: number, openIn?: ViewId[]): Section {
  return {
    inspector: 'listing', key, title, order, openIn,
    when: (ref) => ref.kind === 'listing' && !!ref.id,
    render: (ref, _data, ctx) => {
      const lid = String(ref.id);
      const hit = listingCards.peek(lid);
      return wrap(key, lid, cardInner(key, hit ? hit.data : null, ctx, lid));
    },
    bind: (root, ref, _data, ctx) => {
      const el = find(root, key);
      if (!el) return;
      const lid = String(ref.id);
      const wire = () => {
        const p = ctx.property(lid);
        const name = (listingCards.peek(lid)?.data?.listing.name) || (p ? p.name : lid);
        el.querySelectorAll<HTMLElement>('[data-gx-jump]').forEach(b => b.addEventListener('click', () => {
          const j = b.dataset.gxJump;
          if (j === 'pds') handoff.propertyDetails(ctx, lid);
          else if (j === 'rm') handoff.revenueManager(ctx, lid);
          else if (j === 'reviews') handoff.reviews(ctx, name);
          else if (j === 'settings') handoff.propertySettings(ctx, lid);
          else if (j === 'optimizer') handoff.optimizer(ctx, lid);
        }));
        el.querySelectorAll<HTMLElement>('[data-gx-go]').forEach(b => b.addEventListener('click', () => {
          const g = b.dataset.gxGo;
          if (g === 'claims') handoff.claims(ctx);
          else if (g === 'resolutions') handoff.resolutions(ctx);
          else handoff.recovery(ctx, null);
        }));
      };
      if (listingCards.peek(lid)) { wire(); return; }
      listingCards.get(lid).then(card => { if (!el.isConnected) return; el.innerHTML = cardInner(key, card, ctx, lid, !card); wire(); });
    },
  };
}
// R115 review: the ★ badge and B7 jumps are why the panel was opened — score + links open in every view
// (secIsOpen matches a view's parent, so 'ops' covers Cleans & Inspections and Maintenance); counts open where they matter
const LISTING_OPEN = ['stays', 'ops', 'guests', 'mtr', 'health'] as ViewId[];
export const listingScoreSection = listingSection('score', 'Review score', 12, LISTING_OPEN);
export const listingCountsSection = listingSection('counts', 'Open items', 14, ['guests', 'health'] as ViewId[]);
export const listingLinksSection = listingSection('links', 'Go to', 16, LISTING_OPEN);
